import { useMemo } from 'react';

import BpkBadge, {
  BADGE_TYPES,
} from '@skyscanner-internal/backpack-web/bpk-component-badge';
import {
  BpkBox,
  BpkSpacing,
  BpkStack,
  BpkVessel,
} from '@skyscanner-internal/backpack-web/bpk-component-layout';
import { useSelector } from 'react-redux';

import getFeatureDecision from '../../../../config';
import { useTranslation } from '../../helpers/hooks/useTranslation';
import { getTripDuration } from '../../selectors';
import AppErrorBoundary from '../AppErrorBoundary';
import { ItineraryCardContainer } from '../ItineraryCard/ItineraryCardContainer';
import TotalCostEstimate from '../TotalCostEstimate/TotalCostEstimate';
import { TotalTripCostDateHeader } from '../TripTotalCostFlightSummary/TotalTripCostDateHeader';
import TripTotalCostFlightSummary from '../TripTotalCostFlightSummary/TripTotalCostFlightSummary';
import TotalTripCostHotelSummary from '../TripTotalCostHotelSummary/TotalTripCostHotelSummary';

const MonthViewCTAsTotalCost = ({ isReturn }: { isReturn: boolean }) => {
  const translate = useTranslation();
  const tripDuration = useSelector(getTripDuration);

  const badgeLabels = useMemo(
    () => ({
      weekend: translate('mv_cheapest_weekend_badge'),
      longWeekend: translate('mv_cheapest_longweekend_badge'),
      7: translate('mv_cheapest_1week_badge'),
      14: translate('mv_cheapest_2weeks_badge'),
    }),
    [translate],
  );

  const cheapestDurationBadgeLabel = tripDuration
    ? badgeLabels[tripDuration as keyof typeof badgeLabels]
    : undefined;

  const shouldShowItineraryCard = Boolean(
    getFeatureDecision('ENABLE_DEAL_HUNTER_LITE', false),
  );

  const shouldShowCheapestDurationBadge =
    getFeatureDecision('ENABLE_CHEAPEST_DATES', false) &&
    Boolean(cheapestDurationBadgeLabel) &&
    !shouldShowItineraryCard;

  return (
    <BpkVessel
      data-testid="month-view-variant-total-trip-cost__ctas"
      data-percy-id="ctas"
    >
      <TotalTripCostDateHeader />
      {shouldShowCheapestDurationBadge && cheapestDurationBadgeLabel && (
        <BpkBox marginBlockEnd={BpkSpacing.Base}>
          <BpkBadge type={BADGE_TYPES.brand}>
            {cheapestDurationBadgeLabel}
          </BpkBadge>
        </BpkBox>
      )}
      {shouldShowItineraryCard && <ItineraryCardContainer />}
      <BpkStack direction="column" gap={BpkSpacing.None}>
        <AppErrorBoundary location="monthviewFlightPrice" prefix="Capybara_">
          <TripTotalCostFlightSummary />
        </AppErrorBoundary>
        <AppErrorBoundary location="monthviewHotelPrice" prefix="Capybara_">
          <TotalTripCostHotelSummary isReturn={isReturn} />
        </AppErrorBoundary>
        {isReturn && (
          <AppErrorBoundary location="monthviewCostEstimate" prefix="Capybara_">
            <TotalCostEstimate />
          </AppErrorBoundary>
        )}
      </BpkStack>
    </BpkVessel>
  );
};

export default MonthViewCTAsTotalCost;
